import React, { useEffect, useState, useRef } from 'react';
import { UserIcon, ShieldIcon, BellIcon, CameraIcon, SaveIcon, EyeIcon, EyeOffIcon, CheckIcon, MapPinIcon, DollarSignIcon, ShoppingCartIcon, FileTextIcon, UsersIcon, CalendarIcon, LockIcon, KeyIcon, AlertTriangleIcon, GlobeIcon, Loader2Icon, SettingsIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { supabaseAdmin } from '../lib/supabase';
import { getGoogleApiKey, setGoogleApiKey, validateGoogleApiKey, hasGoogleApiKey } from '../lib/googleMaps';
type Tab = 'perfil' | 'seguridad' | 'notificaciones' | 'integraciones';
const AVATAR_KEY = 'ventastrack_avatar';
const NOTIFS_KEY = 'ventastrack_notifs';
const defaultNotifs = {
  visitasCompletadas: true,
  nuevosCobros: true,
  nuevasVentas: false,
  reportesDiarios: true,
  alertasEquipo: false,
  recordatoriosVisita: true
};
export type NotifPrefs = typeof defaultNotifs;
export function loadNotifPrefs(): NotifPrefs {
  try {
    const saved = localStorage.getItem(NOTIFS_KEY);
    if (saved) return {
      ...defaultNotifs,
      ...JSON.parse(saved)
    };
  } catch {}
  return defaultNotifs;
}
export function loadAvatarUrl(userId?: string): string {
  if (userId) {
    const userSpecific = localStorage.getItem(`ventastrack_avatar_${userId}`);
    if (userSpecific) return userSpecific;
  }
  // Don't fall back to global key — each user has their own avatar
  return '';
}
export function SettingsPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // Permission helpers for settings sub-tabs
  const canSeePerfil = isAdmin || user?.permisos?.settings_perfil === true || user?.permisos?.settings_perfil === undefined;
  const canSeeSeguridad = isAdmin || user?.permisos?.settings_seguridad === true || user?.permisos?.settings_seguridad === undefined;
  const canSeeNotificaciones = isAdmin || user?.permisos?.settings_notificaciones === true || user?.permisos?.settings_notificaciones === undefined;
  const canSeeIntegraciones = isAdmin || user?.permisos?.settings_integraciones === true;
  const [activeTab, setActiveTab] = useState<Tab>('perfil');
  // Perfil
  const [avatarUrl, setAvatarUrl] = useState<string>(() => loadAvatarUrl(user?.id));
  const [nombre, setNombre] = useState(user?.nombre || '');
  // Cargar avatar desde Supabase al iniciar (para sincronizar entre navegadores)
  useEffect(() => {
    if (!user?.id) return;
    const loadFromSupabase = async () => {
      const {
        data
      } = await supabaseAdmin.from('profiles').select('avatar_url').eq('id', user.id).single();
      if (data?.avatar_url) {
        setAvatarUrl(data.avatar_url);
        localStorage.setItem(`ventastrack_avatar_${user.id}`, data.avatar_url);
        window.dispatchEvent(new Event('avatar-updated'));
      }
    };
    loadFromSupabase();
  }, [user?.id]);
  const [email, setEmail] = useState(user?.email || '');
  const [perfilSaved, setPerfilSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Seguridad
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  // Notificaciones
  const [notifs, setNotifs] = useState<NotifPrefs>(loadNotifPrefs);
  const [notifSaved, setNotifSaved] = useState(false);
  // Integraciones
  const [googleKey, setGoogleKey] = useState(getGoogleApiKey);
  const [googleKeyInput, setGoogleKeyInput] = useState(getGoogleApiKey);
  const [isValidatingKey, setIsValidatingKey] = useState(false);
  const [keyValidation, setKeyValidation] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [integrationSaved, setIntegrationSaved] = useState(false);
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return 0;
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 10) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd) || /[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };
  const strengthColors = ['bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500'];
  const strengthLabels = ['Muy débil', 'Débil', 'Buena', 'Fuerte'];
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async ev => {
      const base64 = ev.target?.result as string;
      setAvatarUrl(base64);
      if (user?.id) {
        localStorage.setItem(`ventastrack_avatar_${user.id}`, base64);
        // Guardar en Supabase para sincronizar entre navegadores
        await supabaseAdmin.from('profiles').update({
          avatar_url: base64
        }).eq('id', user.id);
      }
      window.dispatchEvent(new Event('avatar-updated'));
    };
    reader.readAsDataURL(file);
  };
  const handleSavePerfil = () => {
    setPerfilSaved(true);
    setTimeout(() => setPerfilSaved(false), 2500);
  };
  const handleSavePassword = () => {
    setPasswordError('');
    if (!currentPassword) {
      setPasswordError('Ingresa tu contraseña actual');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    addActivityLog('Cambió contraseña', 'Actualización de contraseña');
    setPasswordSaved(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => setPasswordSaved(false), 2500);
  };
  const handleToggleNotif = (key: keyof NotifPrefs) => {
    setNotifs(prev => {
      const updated = {
        ...prev,
        [key]: !prev[key]
      };
      localStorage.setItem(NOTIFS_KEY, JSON.stringify(updated));
      window.dispatchEvent(new Event('notifs-updated'));
      return updated;
    });
  };
  const handleSaveNotifs = () => {
    localStorage.setItem(NOTIFS_KEY, JSON.stringify(notifs));
    window.dispatchEvent(new Event('notifs-updated'));
    setNotifSaved(true);
    setTimeout(() => setNotifSaved(false), 2500);
  };
  const handleValidateGoogleKey = async () => {
    if (!googleKeyInput.trim()) return;
    setIsValidatingKey(true);
    setKeyValidation('idle');
    try {
      const isValid = await validateGoogleApiKey(googleKeyInput.trim());
      setKeyValidation(isValid ? 'valid' : 'invalid');
    } catch {
      setKeyValidation('invalid');
    } finally {
      setIsValidatingKey(false);
    }
  };
  const handleSaveGoogleKey = () => {
    setGoogleApiKey(googleKeyInput.trim());
    setGoogleKey(googleKeyInput.trim());
    addActivityLog('Guardó API Key Google Maps', 'Integración Google Maps configurada');
    setIntegrationSaved(true);
    setTimeout(() => setIntegrationSaved(false), 2500);
  };
  const handleRemoveGoogleKey = () => {
    setGoogleApiKey('');
    setGoogleKey('');
    setGoogleKeyInput('');
    setKeyValidation('idle');
    addActivityLog('Eliminó API Key Google Maps', 'Integración Google Maps desconectada');
    setIntegrationSaved(true);
    setTimeout(() => setIntegrationSaved(false), 2500);
  };
  const notifItems: {
    key: keyof NotifPrefs;
    label: string;
    desc: string;
    icon: React.ReactNode;
    color: string;
  }[] = [{
    key: 'visitasCompletadas',
    label: 'Visitas completadas',
    desc: 'Cuando un vendedor completa una visita',
    icon: <MapPinIcon className="w-4 h-4" data-id="element-3555" />,
    color: 'bg-[#eef2fc] text-[#2952C4]'
  }, {
    key: 'nuevosCobros',
    label: 'Nuevos cobros',
    desc: 'Alertas de cobros registrados en el sistema',
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-3556" />,
    color: 'bg-emerald-100 text-emerald-600'
  }, {
    key: 'nuevasVentas',
    label: 'Nuevas ventas',
    desc: 'Alertas de ventas registradas',
    icon: <ShoppingCartIcon className="w-4 h-4" data-id="element-3557" />,
    color: 'bg-blue-100 text-blue-600'
  }, {
    key: 'reportesDiarios',
    label: 'Reportes diarios',
    desc: 'Resumen diario de actividad del equipo',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-3558" />,
    color: 'bg-[#d5e0f7] text-[#1E3D96]'
  }, {
    key: 'alertasEquipo',
    label: 'Alertas del equipo',
    desc: 'Actividad y cambios del equipo comercial',
    icon: <UsersIcon className="w-4 h-4" data-id="element-3559" />,
    color: 'bg-amber-100 text-amber-600'
  }, {
    key: 'recordatoriosVisita',
    label: 'Recordatorios de visita',
    desc: 'Recordatorios de próximas visitas programadas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-3560" />,
    color: 'bg-rose-100 text-rose-600'
  }];
  const allTabs: {
    id: Tab;
    label: string;
    icon: React.ReactNode;
    visible: boolean;
  }[] = [{
    id: 'perfil',
    label: 'Perfil',
    icon: <UserIcon className="w-5 h-5" data-id="element-3561" />,
    visible: canSeePerfil
  }, {
    id: 'seguridad',
    label: 'Seguridad',
    icon: <ShieldIcon className="w-5 h-5" data-id="element-3562" />,
    visible: canSeeSeguridad
  }, {
    id: 'notificaciones',
    label: 'Notificaciones',
    icon: <BellIcon className="w-5 h-5" data-id="element-3563" />,
    visible: canSeeNotificaciones
  }, {
    id: 'integraciones',
    label: 'Integraciones',
    icon: <GlobeIcon className="w-5 h-5" data-id="element-3564" />,
    visible: canSeeIntegraciones
  }];
  const tabs = allTabs.filter(t => t.visible);
  // Auto-select first visible tab if current is hidden
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);
  const activeNotifCount = Object.values(notifs).filter(Boolean).length;
  const pwdStrength = getPasswordStrength(newPassword);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-3565">
      <PageHeader title="Configuración" subtitle="Ajustes de cuenta y preferencias del sistema" icon={<SettingsIcon className="w-6 h-6" data-id="element-3567" />} data-id="element-3566" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 animate-stagger-1" data-id="element-3568">
        {/* Sidebar */}
        <div className="md:col-span-1 space-y-4" data-id="element-3569">
          {/* User card */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center" data-id="element-3570">
            <div className="relative inline-block mb-4" data-id="element-3571">
              <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center shadow-lg mx-auto" style={{
              background: 'linear-gradient(135deg, #3B65D7, #2952C4)'
            }} data-id="element-3572">
                {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-3573" /> : <span className="text-2xl font-bold text-white" data-id="element-3574">
                    {getInitials(nombre || user?.nombre || '??')}
                  </span>}
              </div>
              <button onClick={() => fileInputRef.current?.click()} className="absolute -bottom-1.5 -right-1.5 w-8 h-8 rounded-xl flex items-center justify-center shadow-lg transition-colors" style={{
              backgroundColor: '#2952C4'
            }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E3D96'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2952C4'} data-id="element-3575">
                <CameraIcon className="w-3.5 h-3.5 text-white" data-id="element-3576" />
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} data-id="element-3577" />
            </div>
            <p className="font-bold text-slate-900 font-heading" data-id="element-3578">
              {user?.nombre}
            </p>
            <p className="text-sm text-slate-500 mt-0.5" data-id="element-3579">{user?.rolNombre}</p>
            <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-full border border-emerald-200" data-id="element-3580">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-3581" />
              Activo
            </span>
          </div>

          {/* Tab navigation */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3582">
            {tabs.map((tab, idx) => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-5 py-4 text-sm font-medium transition-all ${idx < tabs.length - 1 ? 'border-b border-slate-100' : ''}`} style={activeTab === tab.id ? {
            backgroundColor: '#eef2fc',
            color: '#1E3D96',
            borderRight: '2px solid #2952C4'
          } : {}} onMouseEnter={e => {
            if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '#f8fafc';
          }} onMouseLeave={e => {
            if (activeTab !== tab.id) e.currentTarget.style.backgroundColor = '';
          }} data-id="element-3583">
                <span style={activeTab === tab.id ? {
              color: '#2952C4'
            } : {
              color: '#94a3b8'
            }} data-id="element-3584">
                  {tab.icon}
                </span>
                <span className={activeTab === tab.id ? 'font-heading' : 'text-slate-600 font-heading'} data-id="element-3585">
                  {tab.label}
                </span>
                {tab.id === 'notificaciones' && <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{
              backgroundColor: '#eef2fc',
              color: '#2952C4'
            }} data-id="element-3586">
                    {activeNotifCount}
                  </span>}
              </button>)}
          </div>
        </div>

        {/* Right content */}
        <div className="md:col-span-3" data-id="element-3587">
          {/* PERFIL TAB */}
          {activeTab === 'perfil' && canSeePerfil && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3588">
              <div className="px-8 py-6 border-b border-slate-100" style={{
            background: 'linear-gradient(90deg, #eef2fc, #f8fafc)'
          }} data-id="element-3589">
                <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-3590">
                  Información del Perfil
                </h3>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-3591">
                  Actualiza tu información personal y foto de perfil
                </p>
              </div>

              <div className="p-8 space-y-6" data-id="element-3592">
                <div className="flex items-center gap-6 p-5 bg-slate-50 rounded-2xl border border-dashed border-slate-200" data-id="element-3593">
                  <div className="relative flex-shrink-0" data-id="element-3594">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center shadow-md" style={{
                  background: 'linear-gradient(135deg, #3B65D7, #2952C4)'
                }} data-id="element-3595">
                      {avatarUrl ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-id="element-3596" /> : <span className="text-2xl font-bold text-white" data-id="element-3597">
                          {getInitials(nombre || user?.nombre || '??')}
                        </span>}
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-3598">
                    <p className="text-sm font-semibold text-slate-900 mb-1" data-id="element-3599">
                      Foto de perfil
                    </p>
                    <p className="text-xs text-slate-500 mb-3" data-id="element-3600">
                      JPG, PNG o GIF. Máximo 5MB. Se guarda automáticamente.
                    </p>
                    <div className="flex items-center gap-2" data-id="element-3601">
                      <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-xl transition-colors shadow-sm" style={{
                    backgroundColor: '#2952C4'
                  }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1E3D96'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2952C4'} data-id="element-3602">
                        <CameraIcon className="w-4 h-4" data-id="element-3603" />
                        Subir foto
                      </button>
                      {avatarUrl && <button onClick={async () => {
                    setAvatarUrl('');
                    if (user?.id) {
                      localStorage.removeItem(`ventastrack_avatar_${user.id}`);
                      await supabaseAdmin.from('profiles').update({
                        avatar_url: null
                      }).eq('id', user.id);
                    }
                    window.dispatchEvent(new Event('avatar-updated'));
                  }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-colors" data-id="element-3604">
                          Eliminar
                        </button>}
                    </div>
                  </div>
                </div>

                {isAdmin ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-3605">
                    <Input label="Nombre Completo" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Tu nombre completo" data-id="element-3606" />
                    <Input label="Correo Electrónico" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.com" data-id="element-3607" />
                  </div> : <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-3608">
                    <div data-id="element-3609">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3610">
                        Nombre Completo
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-3611">
                        <UserIcon className="w-4 h-4 text-slate-400" data-id="element-3612" />
                        <span className="text-sm text-slate-600" data-id="element-3613">
                          {user?.nombre}
                        </span>
                        <span className="ml-auto text-xs text-slate-400" data-id="element-3614">
                          Solo admin
                        </span>
                      </div>
                    </div>
                    <div data-id="element-3615">
                      <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3616">
                        Correo Electrónico
                      </label>
                      <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-3617">
                        <span className="text-sm text-slate-600" data-id="element-3618">
                          {user?.email || '—'}
                        </span>
                        <span className="ml-auto text-xs text-slate-400" data-id="element-3619">
                          Solo admin
                        </span>
                      </div>
                    </div>
                  </div>}

                <div data-id="element-3620">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3621">
                    Rol del Sistema
                  </label>
                  <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl" data-id="element-3622">
                    <ShieldIcon className="w-4 h-4 text-slate-400" data-id="element-3623" />
                    <span className="text-sm text-slate-600" data-id="element-3624">
                      {user?.rolNombre}
                    </span>
                    <span className="ml-auto text-xs text-slate-400" data-id="element-3625">
                      No editable
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3" data-id="element-3626">
                {perfilSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3627">
                    <CheckIcon className="w-4 h-4" data-id="element-3628" />
                    Cambios guardados
                  </div>}
                <Button onClick={handleSavePerfil} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3630" />} data-id="element-3629">
                  Guardar Cambios
                </Button>
              </div>
            </div>}

          {/* SEGURIDAD TAB */}
          {activeTab === 'seguridad' && canSeeSeguridad && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3631">
              <div className="px-8 py-6 border-b border-slate-100 bg-slate-50" data-id="element-3632">
                <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-3633">
                  Seguridad de la Cuenta
                </h3>
                <p className="text-sm text-slate-500 mt-0.5" data-id="element-3634">
                  Actualiza tu contraseña para mantener tu cuenta segura
                </p>
              </div>

              <div className="p-8 space-y-6" data-id="element-3635">
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-3636">
                  <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-3637" />
                  <div data-id="element-3638">
                    <p className="text-sm font-semibold text-amber-800" data-id="element-3639">
                      Recomendación de seguridad
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5" data-id="element-3640">
                      Usa una contraseña de al menos 10 caracteres con letras,
                      números y símbolos.
                    </p>
                  </div>
                </div>

                <div className="space-y-5" data-id="element-3641">
                  <div data-id="element-3642">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3643">
                      Contraseña Actual
                    </label>
                    <div className="relative" data-id="element-3644">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-3645">
                        <KeyIcon className="w-4 h-4" data-id="element-3646" />
                      </div>
                      <input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white" style={{
                    '--tw-ring-color': 'rgba(41,82,196,0.2)',
                    '--tw-border-opacity': '1'
                  } as React.CSSProperties} data-id="element-3647" />
                      <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3648">
                        {showCurrent ? <EyeOffIcon className="w-4 h-4" data-id="element-3649" /> : <EyeIcon className="w-4 h-4" data-id="element-3650" />}
                      </button>
                    </div>
                  </div>

                  <div data-id="element-3651">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3652">
                      Nueva Contraseña
                    </label>
                    <div className="relative" data-id="element-3653">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-3654">
                        <LockIcon className="w-4 h-4" data-id="element-3655" />
                      </div>
                      <input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" className="w-full pl-10 pr-12 py-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white" data-id="element-3656" />
                      <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3657">
                        {showNew ? <EyeOffIcon className="w-4 h-4" data-id="element-3658" /> : <EyeIcon className="w-4 h-4" data-id="element-3659" />}
                      </button>
                    </div>
                    {newPassword && <div className="mt-3" data-id="element-3660">
                        <div className="flex gap-1.5 mb-1.5" data-id="element-3661">
                          {[0, 1, 2, 3].map(i => <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i < pwdStrength ? strengthColors[pwdStrength - 1] : 'bg-slate-200'}`} data-id="element-3662" />)}
                        </div>
                        <p className={`text-xs font-medium ${pwdStrength <= 1 ? 'text-rose-500' : pwdStrength === 2 ? 'text-amber-500' : pwdStrength === 3 ? 'text-amber-600' : 'text-emerald-600'}`} data-id="element-3663">
                          {strengthLabels[pwdStrength - 1] || 'Muy débil'}
                        </p>
                      </div>}
                  </div>

                  <div data-id="element-3664">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3665">
                      Confirmar Nueva Contraseña
                    </label>
                    <div className="relative" data-id="element-3666">
                      <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-3667">
                        <LockIcon className="w-4 h-4" data-id="element-3668" />
                      </div>
                      <input type={showConfirm ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" className={`w-full pl-10 pr-12 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white ${confirmPassword && newPassword ? confirmPassword === newPassword ? 'border-emerald-400' : 'border-rose-400' : 'border-slate-200'}`} data-id="element-3669" />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-3670">
                        {showConfirm ? <EyeOffIcon className="w-4 h-4" data-id="element-3671" /> : <EyeIcon className="w-4 h-4" data-id="element-3672" />}
                      </button>
                    </div>
                    {confirmPassword && newPassword && <p className={`mt-1.5 text-xs font-medium flex items-center gap-1 ${confirmPassword === newPassword ? 'text-emerald-600' : 'text-rose-500'}`} data-id="element-3673">
                        {confirmPassword === newPassword ? <>
                            <CheckIcon className="w-3 h-3" data-id="element-3674" />
                            Las contraseñas coinciden
                          </> : '✗ Las contraseñas no coinciden'}
                      </p>}
                  </div>
                </div>

                {passwordError && <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-3675">
                    <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" data-id="element-3676" />
                    {passwordError}
                  </div>}
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3" data-id="element-3677">
                {passwordSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3678">
                    <CheckIcon className="w-4 h-4" data-id="element-3679" />
                    Contraseña actualizada
                  </div>}
                <Button onClick={handleSavePassword} leftIcon={<ShieldIcon className="w-4 h-4" data-id="element-3681" />} data-id="element-3680">
                  Actualizar Contraseña
                </Button>
              </div>
            </div>}

          {/* NOTIFICACIONES TAB */}
          {activeTab === 'notificaciones' && canSeeNotificaciones && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3682">
              <div className="px-8 py-6 border-b border-slate-100" style={{
            background: 'linear-gradient(90deg, #eef2fc, #f8fafc)'
          }} data-id="element-3683">
                <div className="flex items-center justify-between" data-id="element-3684">
                  <div data-id="element-3685">
                    <h3 className="text-lg font-bold text-slate-900 font-heading" data-id="element-3686">
                      Notificaciones
                    </h3>
                    <p className="text-sm text-slate-500 mt-0.5" data-id="element-3687">
                      Controla qué alertas deseas recibir
                    </p>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-3688">
                    <BellIcon className="w-4 h-4" style={{
                  color: '#2952C4'
                }} data-id="element-3689" />
                    <span className="text-sm font-bold text-slate-900" data-id="element-3690">
                      {activeNotifCount}
                    </span>
                    <span className="text-xs text-slate-500" data-id="element-3691">activas</span>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-3" data-id="element-3692">
                {notifItems.map(item => <div key={item.key} className="flex items-center justify-between p-4 rounded-xl border transition-all" style={notifs[item.key] ? {
              borderColor: '#aac1ef',
              backgroundColor: 'rgba(238,242,252,0.5)'
            } : {
              borderColor: '#f1f5f9',
              backgroundColor: 'white'
            }} data-id="element-3693">
                    <div className="flex items-center gap-4" data-id="element-3694">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`} data-id="element-3695">
                        {item.icon}
                      </div>
                      <div data-id="element-3696">
                        <p className="text-sm font-semibold text-slate-900" data-id="element-3697">
                          {item.label}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-3698">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer ml-4 flex-shrink-0" data-id="element-3699">
                      <input type="checkbox" className="sr-only peer" checked={notifs[item.key]} onChange={() => handleToggleNotif(item.key)} data-id="element-3700" />
                      <div className="brand-toggle w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all shadow-inner" data-id="element-3701" />
                    </label>
                  </div>)}
              </div>

              <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-3702">
                <p className="text-sm text-slate-500" data-id="element-3703">
                  <span className="font-semibold text-slate-700" data-id="element-3704">
                    {activeNotifCount}
                  </span>{' '}
                  de {notifItems.length} notificaciones activas
                </p>
                <div className="flex items-center gap-3" data-id="element-3705">
                  {notifSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3706">
                      <CheckIcon className="w-4 h-4" data-id="element-3707" />
                      Guardado
                    </div>}
                  <Button onClick={handleSaveNotifs} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3709" />} data-id="element-3708">
                    Guardar Preferencias
                  </Button>
                </div>
              </div>
            </div>}

          {/* INTEGRACIONES TAB */}
          {activeTab === 'integraciones' && canSeeIntegraciones && <div className="space-y-6" data-id="element-3710">
              {/* Google Maps Card */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-3711">
                <div className="px-8 py-6 border-b border-slate-100" style={{
              background: 'linear-gradient(90deg, #e8f5e9, #f1f8e9)'
            }} data-id="element-3712">
                  <div className="flex items-center gap-4" data-id="element-3713">
                    <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center" data-id="element-3714">
                      <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" data-id="element-3715">
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-3716" />
                        <circle cx="12" cy="9" r="3" fill="white" data-id="element-3717" />
                      </svg>
                    </div>
                    <div data-id="element-3718">
                      <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 font-heading" data-id="element-3719">
                        Google Maps Platform
                        {googleKey && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full" data-id="element-3720">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-3721" />
                            Conectado
                          </span>}
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5" data-id="element-3722">
                        Mapas precisos, autocomplete de direcciones y GPS en
                        República Dominicana
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-8 space-y-6" data-id="element-3723">
                  {/* Benefits */}
                  <div className="grid grid-cols-2 gap-3" data-id="element-3724">
                    {[{
                  icon: '🗺️',
                  label: 'Mapas actualizados RD',
                  desc: 'Los más precisos disponibles'
                }, {
                  icon: '🔍',
                  label: 'Autocomplete',
                  desc: 'Búsqueda moderna de direcciones'
                }, {
                  icon: '📍',
                  label: 'GPS preciso',
                  desc: 'Ubicación exacta de farmacias'
                }, {
                  icon: '🧭',
                  label: 'Detección de llegada',
                  desc: 'Verificar visitas automáticamente'
                }].map(b => <div key={b.label} className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl" data-id="element-3725">
                        <span className="text-lg" data-id="element-3726">{b.icon}</span>
                        <div data-id="element-3727">
                          <p className="text-sm font-semibold text-slate-900" data-id="element-3728">
                            {b.label}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-3729">{b.desc}</p>
                        </div>
                      </div>)}
                  </div>

                  {/* API Key Input */}
                  <div data-id="element-3730">
                    <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3731">
                      API Key de Google Maps
                    </label>
                    <div className="flex gap-2" data-id="element-3732">
                      <div className="relative flex-1" data-id="element-3733">
                        <KeyIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3734" />
                        <input type="password" value={googleKeyInput} onChange={e => {
                      setGoogleKeyInput(e.target.value);
                      setKeyValidation('idle');
                    }} placeholder="AIzaSy..." className={`w-full pl-10 pr-4 py-3 text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all bg-slate-50 focus:bg-white font-mono ${keyValidation === 'valid' ? 'border-emerald-400 ring-emerald-200' : keyValidation === 'invalid' ? 'border-rose-400 ring-rose-200' : 'border-slate-200'}`} data-id="element-3735" />
                      </div>
                      <Button variant="secondary" onClick={handleValidateGoogleKey} disabled={!googleKeyInput.trim() || isValidatingKey} data-id="element-3736">
                        {isValidatingKey ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-3737" /> : 'Verificar'}
                      </Button>
                    </div>
                    {keyValidation === 'valid' && <p className="mt-2 text-sm text-emerald-600 font-medium flex items-center gap-1" data-id="element-3738">
                        <CheckIcon className="w-4 h-4" data-id="element-3739" /> API Key válida —
                        Google Maps está listo
                      </p>}
                    {keyValidation === 'invalid' && <p className="mt-2 text-sm text-rose-600 font-medium flex items-center gap-1" data-id="element-3740">
                        <AlertTriangleIcon className="w-4 h-4" data-id="element-3741" /> API Key
                        inválida — Verifica que esté correcta y tenga los APIs
                        habilitados
                      </p>}
                  </div>

                  {/* Instructions */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl" data-id="element-3742">
                    <p className="text-sm font-semibold text-blue-800 mb-2" data-id="element-3743">
                      ¿Cómo obtener tu API Key?
                    </p>
                    <ol className="text-xs text-blue-700 space-y-1.5 list-decimal list-inside" data-id="element-3744">
                      <li data-id="element-3745">
                        Ve a{' '}
                        <a href="https://console.cloud.google.com/apis" target="_blank" rel="noopener noreferrer" className="underline font-semibold hover:text-blue-900" data-id="element-3746">
                          Google Cloud Console
                        </a>
                      </li>
                      <li data-id="element-3747">Crea un proyecto nuevo o selecciona uno existente</li>
                      <li data-id="element-3748">
                        Habilita estos APIs:{' '}
                        <strong data-id="element-3749">Maps JavaScript API</strong>,{' '}
                        <strong data-id="element-3750">Places API</strong>,{' '}
                        <strong data-id="element-3751">Geocoding API</strong>
                      </li>
                      <li data-id="element-3752">
                        Ve a "Credenciales" → "Crear credenciales" → "API Key"
                      </li>
                      <li data-id="element-3753">Copia la API Key y pégala aquí</li>
                    </ol>
                  </div>
                </div>

                <div className="px-8 py-5 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-3754">
                  <div data-id="element-3755">
                    {googleKey && <button onClick={handleRemoveGoogleKey} className="text-sm text-rose-600 hover:text-rose-700 font-medium hover:underline" data-id="element-3756">
                        Desconectar Google Maps
                      </button>}
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3757">
                    {integrationSaved && <div className="flex items-center gap-2 text-emerald-600 text-sm font-semibold" data-id="element-3758">
                        <CheckIcon className="w-4 h-4" data-id="element-3759" />
                        Guardado
                      </div>}
                    <Button onClick={handleSaveGoogleKey} disabled={!googleKeyInput.trim()} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-3761" />} data-id="element-3760">
                      Guardar API Key
                    </Button>
                  </div>
                </div>
              </div>

              {/* Status info */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6" data-id="element-3762">
                <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-3763">
                  Estado de Integraciones
                </h4>
                <div className="space-y-3" data-id="element-3764">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-3765">
                    <div className="flex items-center gap-3" data-id="element-3766">
                      <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center" data-id="element-3767">
                        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" data-id="element-3768">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-3769" />
                          <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-3770" />
                        </svg>
                      </div>
                      <div data-id="element-3771">
                        <p className="text-sm font-medium text-slate-900" data-id="element-3772">
                          Google Maps
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-3773">
                          Mapas, Places, Geocoding
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full ${googleKey ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'}`} data-id="element-3774">
                      <span className={`w-1.5 h-1.5 rounded-full ${googleKey ? 'bg-emerald-500' : 'bg-slate-400'}`} data-id="element-3775" />
                      {googleKey ? 'Activo' : 'No configurado'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-3776">
                    <div className="flex items-center gap-3" data-id="element-3777">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center" data-id="element-3778">
                        <MapPinIcon className="w-4 h-4 text-blue-600" data-id="element-3779" />
                      </div>
                      <div data-id="element-3780">
                        <p className="text-sm font-medium text-slate-900" data-id="element-3781">
                          Photon + OSM
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-3782">
                          Búsqueda gratuita (fallback)
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-3783">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-3784" />
                      Siempre activo
                    </span>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}